import { useState, useEffect, useRef } from "react";
import { 
  Play, 
  Copy, 
  Check, 
  RotateCcw,
  Download,
  Workflow,
  Building2,
  CreditCard,
  Sparkles,
  Save,
  FolderOpen,
  Trash2,
  Loader2,
  Send,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Minimize2,
  Move
} from "lucide-react";
import { toast } from "sonner";
import { useApi } from "@/lib/api";
import MermaidDiagram from "@/components/MermaidDiagram";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

// Predefined business flows
const PREDEFINED_FLOWS = {
  marketplace_managed: {
    id: "marketplace_managed",
    name: "Marketplace-Managed Compliance",
    description: "Flow where marketplace handles compliance administration on seller's behalf",
    icon: Building2,
    color: "bg-violet-100 text-violet-700",
    diagram: `flowchart TD
    subgraph Seller["Seller Actions"]
        A[Opt into MP-managed service]
        B[Sign Master Agreement + PoA]
        C[Provide entity & packaging data]
        G[Monitor dashboard & retain evidence]
    end
    
    subgraph Marketplace["Marketplace Actions"]
        D[Execute registrations & reporting]
        E[Calculate fees]
        F[Collect & remit to PROs]
        H[Provide dashboard & logs]
    end
    
    subgraph Authorities["Authorities/PROs"]
        I[(National Registers)]
        J[(PRO Schemes)]
    end
    
    A --> B
    B --> C
    C --> D
    D --> I
    D --> E
    E --> F
    F --> J
    D --> H
    H --> G
    
    style A fill:#ddd6fe,stroke:#8b5cf6
    style B fill:#ddd6fe,stroke:#8b5cf6
    style C fill:#ddd6fe,stroke:#8b5cf6
    style G fill:#ddd6fe,stroke:#8b5cf6
    style D fill:#fde68a,stroke:#f59e0b
    style E fill:#fde68a,stroke:#f59e0b
    style F fill:#fde68a,stroke:#f59e0b
    style H fill:#fde68a,stroke:#f59e0b
    style I fill:#bbf7d0,stroke:#22c55e
    style J fill:#bbf7d0,stroke:#22c55e`
  },
  pay_on_behalf: {
    id: "pay_on_behalf",
    name: "Pay-on-Behalf Model",
    description: "Marketplace pays PRO for covered streams and recovers costs from seller",
    icon: CreditCard,
    color: "bg-orange-100 text-orange-700",
    diagram: `flowchart TD
    subgraph SellerMain["Seller - Main Flow"]
        A[Sell under PoB model]
        C[Accept terms - auto enrollment]
    end
    
    subgraph SellerParallel["Seller - Parallel Obligations"]
        S1[Register in national registers]
        S2[Contract dual system directly]
        S3[Pay non-covered obligations]
    end
    
    subgraph Marketplace["Marketplace Actions"]
        B[Hold PRO contract - covered stream]
        D[Calculate fees from listings]
        E[Report & pay PRO - covered only]
        F[Recover via automatic deductions]
    end
    
    subgraph Shared["Shared"]
        G[Audit trail & reconciliation]
    end
    
    subgraph External["External"]
        PRO[(PRO Schemes)]
        REG[(National Registers)]
        DUAL[(Dual Systems)]
    end
    
    A --> C
    C --> D
    B --> E
    D --> E
    E --> PRO
    E --> F
    F --> G
    
    A -.-> S1
    S1 --> REG
    S1 --> S2
    S2 --> DUAL
    S2 --> S3
    
    style A fill:#ddd6fe,stroke:#8b5cf6
    style C fill:#ddd6fe,stroke:#8b5cf6
    style S1 fill:#fecaca,stroke:#ef4444
    style S2 fill:#fecaca,stroke:#ef4444
    style S3 fill:#fecaca,stroke:#ef4444
    style B fill:#fde68a,stroke:#f59e0b
    style D fill:#fde68a,stroke:#f59e0b
    style E fill:#fde68a,stroke:#f59e0b
    style F fill:#fde68a,stroke:#f59e0b
    style G fill:#e0e7ff,stroke:#6366f1
    style PRO fill:#bbf7d0,stroke:#22c55e
    style REG fill:#bbf7d0,stroke:#22c55e
    style DUAL fill:#bbf7d0,stroke:#22c55e`
  }
};

const STARTER_TEMPLATES = [
  {
    name: "Simple Flow",
    code: `flowchart LR
    A[Start] --> B{Decision}
    B -->|Yes| C[Action 1]
    B -->|No| D[Action 2]
    C --> E[End]
    D --> E`
  },
  {
    name: "Process Steps",
    code: `flowchart TD
    A[Step 1: Input] --> B[Step 2: Process]
    B --> C[Step 3: Validate]
    C --> D{Valid?}
    D -->|Yes| E[Step 4: Output]
    D -->|No| B`
  },
  {
    name: "Swimlanes",
    code: `flowchart TB
    subgraph Team1["Team A"]
        A1[Task 1] --> A2[Task 2]
    end
    subgraph Team2["Team B"]
        B1[Task 3] --> B2[Task 4]
    end
    A2 --> B1`
  }
];

// Zoom-enabled diagram viewer component
function DiagramViewer({ chart, isFullscreen, onToggleFullscreen }) {
  const [zoom, setZoom] = useState(100);
  const [isPanning, setIsPanning] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 25, 300));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 25, 25));
  const handleResetZoom = () => { setZoom(100); setPosition({ x: 0, y: 0 }); };
  const handleZoomSlider = (value) => setZoom(value);

  // Handle escape key to exit fullscreen
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isFullscreen) {
        onToggleFullscreen();
      }
      // Keyboard shortcuts for zoom
      if ((e.ctrlKey || e.metaKey) && e.key === '=') {
        e.preventDefault();
        handleZoomIn();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === '-') {
        e.preventDefault();
        handleZoomOut();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === '0') {
        e.preventDefault();
        handleResetZoom();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen, onToggleFullscreen]);

  const handleMouseDown = (e) => {
    if (e.button === 0) {
      setIsPanning(true);
      setStartPos({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isPanning) {
      setPosition({
        x: e.clientX - startPos.x,
        y: e.clientY - startPos.y
      });
    }
  };

  const handleMouseUp = () => setIsPanning(false);

  const handleWheel = (e) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      if (e.deltaY < 0) {
        handleZoomIn();
      } else {
        handleZoomOut();
      }
    }
  };

  return (
    <div 
      className={`relative ${isFullscreen ? 'fixed inset-0 z-50 bg-white dark:bg-zinc-900' : ''}`}
      data-testid="diagram-viewer"
    >
      {/* Controls Toolbar */}
      <div className={`absolute ${isFullscreen ? 'top-4 right-4' : 'top-3 right-3'} z-10 flex items-center gap-1 bg-white/95 backdrop-blur-sm rounded-xl p-1.5 shadow-lg border border-zinc-200`}>
        {/* Zoom Out */}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleZoomOut}
          disabled={zoom <= 25}
          className="h-8 w-8 p-0 hover:bg-zinc-100"
          title="Zoom Out (Ctrl -)"
          data-testid="zoom-out-btn"
        >
          <ZoomOut className="w-4 h-4" />
        </Button>
        
        {/* Zoom Slider */}
        <div className="flex items-center gap-2 px-2">
          <input
            type="range"
            min="25"
            max="300"
            step="25"
            value={zoom}
            onChange={(e) => handleZoomSlider(Number(e.target.value))}
            className="w-20 h-1.5 bg-zinc-200 rounded-full appearance-none cursor-pointer accent-violet-600"
            title="Drag to adjust zoom"
            data-testid="zoom-slider"
          />
          <span className="text-xs font-medium text-zinc-600 min-w-[40px] text-center">{zoom}%</span>
        </div>
        
        {/* Zoom In */}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleZoomIn}
          disabled={zoom >= 300}
          className="h-8 w-8 p-0 hover:bg-zinc-100"
          title="Zoom In (Ctrl +)"
          data-testid="zoom-in-btn"
        >
          <ZoomIn className="w-4 h-4" />
        </Button>
        
        <div className="w-px h-6 bg-zinc-200 mx-1" />
        
        {/* Reset View */}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleResetZoom}
          className="h-8 w-8 p-0 hover:bg-zinc-100"
          title="Reset View (Ctrl 0)"
          data-testid="reset-view-btn"
        >
          <Move className="w-4 h-4" />
        </Button>
        
        {/* Fullscreen Toggle */}
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggleFullscreen}
          className={`h-8 w-8 p-0 hover:bg-zinc-100 ${isFullscreen ? 'text-violet-600' : ''}`}
          title={isFullscreen ? "Exit Fullscreen (Esc)" : "Fullscreen"}
          data-testid="fullscreen-btn"
        >
          {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
        </Button>
      </div>

      {/* Fullscreen Header */}
      {isFullscreen && (
        <div className="absolute top-4 left-4 z-10 flex items-center gap-3 bg-white/95 backdrop-blur-sm rounded-xl px-4 py-2 shadow-lg border border-zinc-200">
          <Workflow className="w-5 h-5 text-violet-600" />
          <span className="font-medium text-zinc-800">Business Flow Viewer</span>
          <Badge variant="secondary" className="text-xs">Press Esc to exit</Badge>
        </div>
      )}

      {/* Diagram Container */}
      <div
        ref={containerRef}
        className={`overflow-hidden bg-zinc-50 rounded-lg ${isFullscreen ? 'h-screen' : 'min-h-[500px]'} cursor-grab active:cursor-grabbing select-none`}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
        data-testid="diagram-canvas"
      >
        <div
          className="flex items-center justify-center p-6 transition-transform duration-100"
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${zoom / 100})`,
            transformOrigin: 'center center',
            minHeight: isFullscreen ? '100vh' : '500px'
          }}
        >
          <MermaidDiagram chart={chart} className="w-full" />
        </div>
      </div>

      {/* Help Hint - Only in normal mode */}
      {!isFullscreen && (
        <div className="flex items-center justify-center gap-4 text-xs text-zinc-400 mt-2">
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-zinc-100 rounded text-zinc-500 font-mono">Ctrl</kbd>
            <span>+ Scroll to zoom</span>
          </span>
          <span>•</span>
          <span>Drag to pan</span>
          <span>•</span>
          <span className="flex items-center gap-1">
            <Maximize2 className="w-3 h-3" />
            <span>Fullscreen for large diagrams</span>
          </span>
        </div>
      )}
    </div>
  );
}

export default function BusinessFlows() {
  const api = useApi();
  const [activeTab, setActiveTab] = useState("predefined");
  const [selectedFlow, setSelectedFlow] = useState("marketplace_managed");
  const [customCode, setCustomCode] = useState(STARTER_TEMPLATES[0].code);
  const [copied, setCopied] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showEditorPanel, setShowEditorPanel] = useState(true);
  
  // AI Assistant state
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [hasAiResult, setHasAiResult] = useState(false);
  
  // Saved flows state
  const [savedFlows, setSavedFlows] = useState([]);
  const [loadingFlows, setLoadingFlows] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveDescription, setSaveDescription] = useState("");
  const [saveTags, setSaveTags] = useState("");

  useEffect(() => {
    fetchSavedFlows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSavedFlows = async () => {
    setLoadingFlows(true);
    try {
      const response = await api.get("/api/flows/");
      setSavedFlows(response.data.flows || []);
    } catch (error) {
      console.error("Error fetching flows:", error);
    } finally {
      setLoadingFlows(false);
    }
  };

  const handleCopy = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Failed to copy");
    }
  };

  const handleDownloadSVG = () => {
    const svgElement = document.querySelector(".mermaid-container svg");
    if (!svgElement) {
      toast.error("No diagram to download");
      return;
    }

    const svgData = new XMLSerializer().serializeToString(svgElement);
    const blob = new Blob([svgData], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement("a");
    link.href = url;
    link.download = "business-flow.svg";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success("SVG downloaded");
  };

  const handleLoadTemplate = (template) => {
    setCustomCode(template.code);
    setHasAiResult(false);
    toast.success(`Loaded "${template.name}" template`);
  };

  const handleAIConvert = async () => {
    if (!aiPrompt.trim()) {
      toast.error("Please enter a process description");
      return;
    }

    setAiLoading(true);
    try {
      const response = await api.post("/api/flows/convert", {
        text: aiPrompt
      });
      
      if (response.data.success) {
        setCustomCode(response.data.mermaid_code);
        setHasAiResult(true);
        setShowEditorPanel(false); // Collapse editor to focus on diagram
        toast.success("Converted to Mermaid diagram!");
      } else {
        toast.error(response.data.error || "Conversion failed");
      }
    } catch (error) {
      console.error("AI conversion error:", error);
      toast.error(error.response?.data?.detail || "Failed to convert text");
    } finally {
      setAiLoading(false);
    }
  };

  const handleSaveFlow = async () => {
    if (!saveName.trim()) {
      toast.error("Please enter a name for the flow");
      return;
    }

    try {
      const tags = saveTags.split(",").map(t => t.trim()).filter(t => t);
      await api.post("/api/flows/", {
        name: saveName,
        description: saveDescription,
        mermaid_code: customCode,
        tags
      });
      
      toast.success("Flow saved successfully!");
      setShowSaveDialog(false);
      setSaveName("");
      setSaveDescription("");
      setSaveTags("");
      fetchSavedFlows();
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save flow");
    }
  };

  const handleLoadFlow = (flow) => {
    setCustomCode(flow.mermaid_code);
    setActiveTab("custom");
    setHasAiResult(true);
    setShowEditorPanel(false);
    toast.success(`Loaded "${flow.name}"`);
  };

  const handleDeleteFlow = async (flowId, flowName) => {
    if (!window.confirm(`Delete "${flowName}"?`)) return;
    
    try {
      await api.delete(`/api/flows/${flowId}`);
      toast.success("Flow deleted");
      fetchSavedFlows();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete flow");
    }
  };

  const currentFlow = PREDEFINED_FLOWS[selectedFlow];

  return (
    <div className="content-wrapper" data-testid="business-flows-page">
      <div className="page-header">
        <h1 className="page-title">Business Flows</h1>
        <p className="page-subtitle">
          Visualize and prototype EPR compliance workflows using interactive diagrams
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-6">
          <TabsTrigger value="predefined" data-testid="tab-predefined">
            <Workflow className="w-4 h-4 mr-2" />
            Predefined Flows
          </TabsTrigger>
          <TabsTrigger value="custom" data-testid="tab-custom">
            <Sparkles className="w-4 h-4 mr-2" />
            AI Editor
          </TabsTrigger>
          <TabsTrigger value="saved" data-testid="tab-saved">
            <FolderOpen className="w-4 h-4 mr-2" />
            Saved ({savedFlows.length})
          </TabsTrigger>
        </TabsList>

        {/* Predefined Flows Tab */}
        <TabsContent value="predefined" className="space-y-6">
          <div className="flex flex-wrap gap-3">
            {Object.values(PREDEFINED_FLOWS).map((flow) => {
              const Icon = flow.icon;
              return (
                <button
                  key={flow.id}
                  onClick={() => setSelectedFlow(flow.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                    selectedFlow === flow.id
                      ? "border-violet-500 bg-violet-50"
                      : "border-zinc-200 hover:border-zinc-300"
                  }`}
                  data-testid={`flow-btn-${flow.id}`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="font-medium">{flow.name}</span>
                </button>
              );
            })}
          </div>

          <div className="card p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-zinc-800">{currentFlow.name}</h2>
                <p className="text-zinc-500 mt-1">{currentFlow.description}</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => handleCopy(currentFlow.diagram)}>
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  Copy
                </Button>
                <Button variant="outline" size="sm" onClick={handleDownloadSVG}>
                  <Download className="w-4 h-4" />
                  SVG
                </Button>
              </div>
            </div>

            <DiagramViewer 
              chart={currentFlow.diagram} 
              isFullscreen={isFullscreen}
              onToggleFullscreen={() => setIsFullscreen(!isFullscreen)}
            />

            <div className="mt-4 pt-4 border-t border-zinc-200">
              <div className="flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-violet-200 border border-violet-500"></div>
                  <span className="text-zinc-600">Seller</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-amber-200 border border-amber-500"></div>
                  <span className="text-zinc-600">Marketplace</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-green-200 border border-green-500"></div>
                  <span className="text-zinc-600">External</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-red-200 border border-red-500"></div>
                  <span className="text-zinc-600">Parallel</span>
                </div>
              </div>
            </div>
          </div>

          <Accordion type="single" collapsible>
            <AccordionItem value="code">
              <AccordionTrigger className="text-sm font-medium">View Mermaid Code</AccordionTrigger>
              <AccordionContent>
                <pre className="bg-zinc-900 text-zinc-100 p-4 rounded-lg text-sm overflow-x-auto">
                  <code>{currentFlow.diagram}</code>
                </pre>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </TabsContent>

        {/* AI Editor Tab */}
        <TabsContent value="custom" className="space-y-6">
          {/* AI Assistant - Compact when results exist */}
          <div className={`card p-4 bg-gradient-to-r from-violet-50 to-purple-50 border-violet-200 ${hasAiResult ? '' : 'p-6'}`}>
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-violet-600" />
              <h2 className="text-lg font-semibold text-violet-800">AI Assistant</h2>
              {hasAiResult && (
                <Badge variant="secondary" className="ml-auto">Result generated</Badge>
              )}
            </div>
            
            <div className="flex gap-3">
              <textarea
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                placeholder="Describe your business process... E.g., Seller registers with AR, AR submits documents to national register, register confirms, AR notifies seller"
                className={`flex-1 p-3 rounded-lg border border-violet-200 focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none ${hasAiResult ? 'h-16' : 'h-24'}`}
                data-testid="ai-prompt-input"
              />
              <Button
                onClick={handleAIConvert}
                disabled={aiLoading || !aiPrompt.trim()}
                className="bg-violet-600 hover:bg-violet-700 text-white px-6 self-end"
                data-testid="ai-convert-btn"
              >
                {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                <span className="ml-2">{aiLoading ? "Converting..." : "Convert"}</span>
              </Button>
            </div>
          </div>

          {/* Main Content - Diagram focused layout */}
          <div className={`grid gap-6 ${showEditorPanel ? 'lg:grid-cols-2' : 'grid-cols-1'}`}>
            {/* Editor Panel - Collapsible */}
            {showEditorPanel && (
              <div className="card p-4 lg:order-2">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-semibold text-zinc-800">Code Editor</h2>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setCustomCode(STARTER_TEMPLATES[0].code)}>
                      <RotateCcw className="w-3 h-3" />
                    </Button>
                    <Button variant="default" size="sm" onClick={() => setShowSaveDialog(true)}>
                      <Save className="w-3 h-3 mr-1" />
                      Save
                    </Button>
                  </div>
                </div>

                <div className="flex flex-wrap gap-1 mb-3">
                  {STARTER_TEMPLATES.map((template) => (
                    <Badge
                      key={template.name}
                      variant="outline"
                      className="cursor-pointer hover:bg-zinc-100 text-xs"
                      onClick={() => handleLoadTemplate(template)}
                    >
                      {template.name}
                    </Badge>
                  ))}
                </div>

                <textarea
                  value={customCode}
                  onChange={(e) => setCustomCode(e.target.value)}
                  className="w-full h-[300px] font-mono text-sm bg-zinc-900 text-zinc-100 p-3 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-violet-500"
                  spellCheck={false}
                  data-testid="mermaid-editor"
                />
              </div>
            )}

            {/* Preview Panel - Main focus */}
            <div className={`card p-4 ${showEditorPanel ? 'lg:order-1' : ''}`}>
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-semibold text-zinc-800">Flowchart Preview</h2>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowEditorPanel(!showEditorPanel)}
                  >
                    {showEditorPanel ? "Hide Editor" : "Show Editor"}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleCopy(customCode)}>
                    {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleDownloadSVG}>
                    <Download className="w-3 h-3" />
                  </Button>
                  <Button variant="default" size="sm" onClick={() => setShowSaveDialog(true)}>
                    <Save className="w-3 h-3 mr-1" />
                    Save
                  </Button>
                </div>
              </div>

              <DiagramViewer 
                chart={customCode} 
                isFullscreen={isFullscreen}
                onToggleFullscreen={() => setIsFullscreen(!isFullscreen)}
              />
            </div>
          </div>

          {/* Tips - Compact */}
          <Accordion type="single" collapsible>
            <AccordionItem value="tips">
              <AccordionTrigger className="text-sm font-medium">Syntax Tips & Common Errors</AccordionTrigger>
              <AccordionContent>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-3 bg-violet-50 rounded-lg">
                    <h4 className="font-medium text-violet-800 mb-2 text-sm">Syntax Tips</h4>
                    <ul className="text-xs text-violet-700 space-y-1">
                      <li>• <code className="bg-violet-100 px-1 rounded">flowchart TD</code> top-down, <code className="bg-violet-100 px-1 rounded">LR</code> left-right</li>
                      <li>• <code className="bg-violet-100 px-1 rounded">style NodeID fill:#color</code> for colors</li>
                      <li>• <code className="bg-violet-100 px-1 rounded">subgraph Name[Label]...end</code> for groups</li>
                    </ul>
                  </div>
                  <div className="p-3 bg-amber-50 rounded-lg">
                    <h4 className="font-medium text-amber-800 mb-2 text-sm">Avoid These</h4>
                    <ul className="text-xs text-amber-700 space-y-1">
                      <li>• ❌ HTML tags like <code className="bg-amber-100 px-1 rounded">&lt;br/&gt;</code></li>
                      <li>• ❌ Special arrows <code className="bg-amber-100 px-1 rounded">→</code> use <code className="bg-amber-100 px-1 rounded">--&gt;</code></li>
                      <li>• ✅ Wrap special chars: <code className="bg-amber-100 px-1 rounded">["Text (here)"]</code></li>
                    </ul>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </TabsContent>

        {/* Saved Flows Tab */}
        <TabsContent value="saved" className="space-y-6">
          <div className="card p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-zinc-800">Your Saved Flows</h2>
              <Button variant="outline" size="sm" onClick={fetchSavedFlows} disabled={loadingFlows}>
                {loadingFlows ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                Refresh
              </Button>
            </div>

            {loadingFlows ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
              </div>
            ) : savedFlows.length === 0 ? (
              <div className="text-center py-12 text-zinc-500">
                <FolderOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No saved flows yet.</p>
                <p className="text-sm mt-2">Create a flow in the AI Editor tab and save it!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {savedFlows.map((flow) => (
                  <div key={flow.id} className="border border-zinc-200 rounded-lg p-4 hover:border-violet-300 transition-colors">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-medium text-zinc-800 truncate flex-1">{flow.name}</h3>
                      <button
                        onClick={() => handleDeleteFlow(flow.id, flow.name)}
                        className="text-zinc-400 hover:text-red-500 p-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    {flow.description && (
                      <p className="text-sm text-zinc-500 mb-3 line-clamp-2">{flow.description}</p>
                    )}
                    {flow.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {flow.tags.map((tag, idx) => (
                          <Badge key={idx} variant="secondary" className="text-xs">{tag}</Badge>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center justify-between text-xs text-zinc-400">
                      <span>{new Date(flow.created_at).toLocaleDateString()}</span>
                      <Button variant="outline" size="sm" onClick={() => handleLoadFlow(flow)}>
                        Load
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Save Flow Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Flow</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="flow-name">Name *</Label>
              <Input
                id="flow-name"
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                placeholder="E.g., AR Registration Process"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="flow-description">Description</Label>
              <Input
                id="flow-description"
                value={saveDescription}
                onChange={(e) => setSaveDescription(e.target.value)}
                placeholder="Brief description"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="flow-tags">Tags (comma-separated)</Label>
              <Input
                id="flow-tags"
                value={saveTags}
                onChange={(e) => setSaveTags(e.target.value)}
                placeholder="EPR, Germany, Packaging"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveFlow} disabled={!saveName.trim()}>
              <Save className="w-4 h-4 mr-2" />
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
